import React, { SetStateAction, useCallback, useMemo } from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import { withLocalePrefix } from 'shared/utils/translation';
import {
  fetchSavedSearch,
  createSavedSearch,
  updateSavedSearch,
  setDefaultSavedSearch,
} from 'talent_matching/api';
import { useUserContext } from 'talent_matching/contexts/user_context';
import SavedFilterButtons from 'talent_matching/components/filters/saved_filter_buttons';
import LoadFiltersModal from 'talent_matching/talent_rediscovery/components/filters/load_filters_modal';
import SaveFiltersModal from 'talent_search/talent_rediscovery/modals/save_filters_modal';
import type { GreenhouseFilterPreset } from 'talent_matching/talent_rediscovery/types';
import {
  trackUserAction,
  type TalentMatchingSource,
} from 'talent_matching/lib/tracker';
import styled from 'styled-components';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';

const SubtitleText = styled.span`
  color: ${FullPalette.GREY_800};
`;

const PresetLabel: React.FC<{ label: string }> = ({ label }) => {
  const dashIndex = label.indexOf(' - ');
  if (dashIndex === -1) {
    return <span>{label}</span>;
  }
  return (
    <span>
      {label.slice(0, dashIndex)}
      <SubtitleText>{label.slice(dashIndex)}</SubtitleText>
    </span>
  );
};

const t = withLocalePrefix('talent_rediscovery.filters.labels');

export type SavedFiltersProps = {
  featureType: string;
  savedFilterSet: Item | null;
  setSavedFilterSet: (item: Item | null) => void;
  setSavedSearchOptions: (options: SetStateAction<Item[]>) => void;
  formDefaults: Record<string, unknown>;
  savedSearchOptions: Item[];
  parseSavedSearch: (response: unknown) => Record<string, unknown>;
  buildQueryString: () => string;
  greenhouseFilterPresets?: GreenhouseFilterPreset[];
  // When true, Load/Save/Clear actions also persist the chosen saved_search
  // (or greenhouse_filter_key) as the user's default for this hiring plan +
  // featureType. Off by default so features that don't read from
  // UserDefaultSavedSearch don't write to it.
  rememberLastUsed?: boolean;
  // When set, Load/Save emit analytics events attributed to this surface.
  // Omitted for surfaces not yet wired for analytics (e.g. MyGreenhouse).
  trackingSource?: TalentMatchingSource;
};

const SavedFilters = ({
  featureType,
  savedFilterSet,
  setSavedFilterSet,
  setSavedSearchOptions,
  formDefaults,
  savedSearchOptions,
  parseSavedSearch,
  buildQueryString,
  greenhouseFilterPresets,
  rememberLastUsed = false,
  trackingSource,
}: SavedFiltersProps) => {
  const { reset, setValue } = useFormContext();
  const {
    hiringPlanId,
    organizationId,
    userId,
    paths: { savedSearchesPath, defaultSavedSearchPath },
  } = useUserContext();

  const trackAttribution = useMemo(
    () =>
      trackingSource
        ? { source: trackingSource, organizationId, userId, hiringPlanId }
        : null,
    [trackingSource, organizationId, userId, hiringPlanId]
  );

  const filterNames = useMemo(
    () => savedSearchOptions.map((o) => o.label),
    [savedSearchOptions]
  );

  const dropdownOptions: Item[] = useMemo(() => {
    if (!greenhouseFilterPresets?.length) {
      return savedSearchOptions;
    }

    const greenhouseItems: Item[] = greenhouseFilterPresets.map((preset) => ({
      label: preset.label,
      value: preset.value,
      CustomLabel: PresetLabel,
    }));

    return [
      {
        label: t('greenhouse_suggested_filters'),
        value: 'greenhouse_filters',
        disabled: true,
        items: greenhouseItems,
      },
      {
        label: t('your_filters'),
        value: 'your_filters',
        disabled: true,
        items: savedSearchOptions,
      },
    ];
  }, [greenhouseFilterPresets, savedSearchOptions]);

  const [loadModalOpen, openLoadModal, closeLoadModal] = useModal();
  const [saveModalOpen, openSaveModal, closeSaveModal] = useModal();

  const handleLoadSelect = useCallback(
    async (option: Item | null) => {
      if (!option) {
        setSavedFilterSet(null);
        reset(formDefaults);
        if (rememberLastUsed) {
          // Persist the "empty" preference so next visit starts empty
          // instead of auto-applying the suggested filter.
          setDefaultSavedSearch(defaultSavedSearchPath, {
            hiringPlanId,
            featureType,
            savedSearchId: null,
            greenhouseFilterKey: null,
          });
        }
        return;
      }

      const preset = greenhouseFilterPresets?.find(
        (p) => p.value === option.value
      );

      if (preset) {
        const displayItem = preset.shortLabel
          ? { ...option, label: preset.shortLabel }
          : option;
        setSavedFilterSet(displayItem);
        reset(formDefaults);
        Object.keys(preset.filterValues).forEach((key) => {
          setValue(key, preset.filterValues[key], { shouldDirty: true });
        });
        if (rememberLastUsed) {
          setDefaultSavedSearch(defaultSavedSearchPath, {
            hiringPlanId,
            featureType,
            greenhouseFilterKey: preset.value,
          });
        }
        if (trackAttribution) {
          trackUserAction({
            ...trackAttribution,
            category: 'interaction',
            action: 'filters_loaded',
            metadata: {
              greenhouse_filter_key: preset.value,
              is_suggested_preset: true,
            },
          });
        }
        return;
      }

      setSavedFilterSet(option);

      const response = await fetchSavedSearch(
        savedSearchesPath,
        option.value as unknown as number,
        featureType
      );
      const formFields = parseSavedSearch(response);
      reset(formDefaults);
      Object.keys(formFields).forEach((key) => {
        setValue(key, formFields[key], { shouldDirty: true });
      });

      if (rememberLastUsed) {
        setDefaultSavedSearch(defaultSavedSearchPath, {
          hiringPlanId,
          featureType,
          savedSearchId: Number(option.value),
        });
      }

      if (trackAttribution) {
        trackUserAction({
          ...trackAttribution,
          category: 'interaction',
          action: 'filters_loaded',
          metadata: { saved_search_id: Number(option.value) },
        });
      }
    },
    [
      setSavedFilterSet,
      formDefaults,
      greenhouseFilterPresets,
      savedSearchesPath,
      defaultSavedSearchPath,
      hiringPlanId,
      featureType,
      parseSavedSearch,
      reset,
      setValue,
      rememberLastUsed,
      trackAttribution,
    ]
  );

  const handleSaveFilters = useCallback(
    async (name: string) => {
      const response = await createSavedSearch(
        savedSearchesPath,
        buildQueryString(),
        name,
        featureType
      );
      const newOption = {
        value: response.id.toString(),
        label: response.label,
      };
      setSavedSearchOptions((prev: Item[]) => [newOption, ...prev]);
      setSavedFilterSet(newOption);
      closeSaveModal();

      if (rememberLastUsed) {
        setDefaultSavedSearch(defaultSavedSearchPath, {
          hiringPlanId,
          featureType,
          savedSearchId: Number(response.id),
        });
      }

      if (trackAttribution) {
        trackUserAction({
          ...trackAttribution,
          category: 'interaction',
          action: 'filters_saved',
          metadata: { saved_search_id: Number(response.id) },
        });
      }
    },
    [
      savedSearchesPath,
      defaultSavedSearchPath,
      hiringPlanId,
      buildQueryString,
      featureType,
      setSavedSearchOptions,
      setSavedFilterSet,
      closeSaveModal,
      rememberLastUsed,
      trackAttribution,
    ]
  );

  const handleUpdateFilters = useCallback(async () => {
    if (!savedFilterSet?.value) {
      return;
    }
    await updateSavedSearch(
      savedSearchesPath,
      savedFilterSet.value as unknown as number,
      buildQueryString(),
      savedFilterSet.label,
      featureType
    );
  }, [savedSearchesPath, savedFilterSet, buildQueryString, featureType]);

  const isGreenhousePreset = greenhouseFilterPresets?.some(
    (p) => p.value === savedFilterSet?.value
  );

  return (
    <>
      <SavedFilterButtons
        savedFilterSet={savedFilterSet}
        onLoadFilters={openLoadModal}
        onSaveFilters={openSaveModal}
        onUpdateFilters={isGreenhousePreset ? undefined : handleUpdateFilters}
      />
      <LoadFiltersModal
        isOpen={loadModalOpen}
        onDismiss={closeLoadModal}
        options={dropdownOptions}
        onSelect={handleLoadSelect}
      />
      <SaveFiltersModal
        isOpen={saveModalOpen}
        close={closeSaveModal}
        onSave={handleSaveFilters}
        filterNames={filterNames}
      />
    </>
  );
};

export default SavedFilters;
